import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Lock, CheckCircle2, Loader2 } from "lucide-react";
import { SignaturePad } from "@/components/public/SignaturePad";

const FIELDS = [
  ["account_country", "País de la cuenta bancaria *", "España", true],
  ["bank_name", "Nombre del banco *", "Ej.: BBVA", true],
  ["holder_name", "Nombre completo del titular *", "Como aparece en el banco", true],
  ["id_document", "DNI / NIE / Pasaporte *", "", true],
  ["iban", "IBAN / Nº de cuenta *", "ES00 0000 0000 0000 0000 0000", true],
  ["swift_bic", "SWIFT / BIC", "Obligatorio si la cuenta no es española", false],
  ["routing_number", "Nº de ruta (routing / sort code)", "Solo cuentas de EE. UU., Reino Unido…", false],
  ["holder_address", "Dirección del titular *", "Calle, número, CP, ciudad", true],
  ["email", "Email", "", false],
  ["phone", "Teléfono", "", false],
];

export default function BankFormPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});
  const [signature, setSignature] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/forms/${token}`).then((r) => setInfo(r.data)).catch(() => setError("Este enlace no es válido."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!signature) return setError("Falta tu firma.");
    if (!accept) return setError("Debes aceptar la autorización.");
    setBusy(true);
    setError("");
    try {
      await api.post(`/public/forms/${token}`, { ...form, signature, accept });
      setDone(true);
    } catch (e2) {
      setError(formatApiErrorDetail(e2?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done || info?.status === "completed")
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50" data-testid="bank-form-done">
        <div className="border-2 border-black bg-white p-8 max-w-md text-center">
          <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={40} />
          <h1 className="font-display text-2xl font-bold">Datos recibidos</h1>
          <p className="text-sm text-zinc-600 mt-2">Gracias. Tus datos se han guardado cifrados y de forma confidencial.</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4" data-testid="bank-form-page">
      <form onSubmit={submit} className="max-w-xl mx-auto border-2 border-black bg-white p-6 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">DS Travel Tours · Formulario</div>
        <h1 className="font-display text-3xl font-bold">Datos bancarios</h1>
        {info && <p className="text-sm">Hola <b>{info.person_name}</b>, necesitamos tus datos para poder hacerte las transferencias.</p>}
        <div className="bg-emerald-50 border-l-4 border-emerald-600 p-3 text-xs text-emerald-900 flex gap-2" data-testid="bank-form-security">
          <ShieldCheck size={16} className="shrink-0" />
          Tus datos se guardan de manera segura, cifrados y son clasificados. Solo la administración de DS Travel Tours puede verlos.
        </div>
        {FIELDS.map(([k, label, ph, req]) => (
          <div key={k}>
            <label className="text-[10px] uppercase tracking-widest">{label}</label>
            <Input required={req} placeholder={ph} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              className="h-11 rounded-none border-black mt-1" data-testid={`bank-${k}`} />
          </div>
        ))}
        <div>
          <label className="text-[10px] uppercase tracking-widest">Firma *</label>
          <SignaturePad onChange={setSignature} />
        </div>
        <label className="flex gap-2 text-xs items-start">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5" data-testid="bank-accept" />
          Yo, el titular, autorizo a DS Travel Tours a utilizar esta cuenta para realizarme las transferencias y operaciones derivadas de mi relación con la empresa, y declaro que los datos son verdaderos.
        </label>
        {error && <p className="text-sm text-red-700" data-testid="bank-form-error">{error}</p>}
        <button type="submit" disabled={busy || !info} className="w-full h-12 bg-black text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#E63946] transition-colors inline-flex items-center justify-center gap-2" data-testid="bank-submit">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Lock size={14} />} Enviar de forma segura
        </button>
      </form>
    </div>
  );
}
